import React, { useState, useEffect } from "react";
import "./InvoiceList.css";
import profileIcon from '../../shared/assets/icons/profile.png';
import { useNavigate } from 'react-router-dom';
import SearchInput from '../../components/SearchInput';
import { invoiceService } from './services/invoiceService';
import DataTableGrid from '../../shared/components/DataTableGrid';
import HeaderNav from "../../shared/components/HeaderNav";
import MainLoader from "../../shared/components/MainLoader";
import MobileFooterNav from "../../shared/components/MobileFooterNav";
import { IoArrowUndoOutline } from "react-icons/io5";
import { formatCurrency, formatDate } from '../../shared/utils/localeHelper';
import { LuFilter } from "react-icons/lu";
import { LuDownload } from "react-icons/lu";
import InvoiceFilterModal from '../../components/InvoiceFilterModal';
import PaymentModal from '../payment/PaymentModal';
import { MdPayment } from 'react-icons/md';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { paymentService } from './services/paymentService';
import { getOrgGuid } from '../../shared/utils/authStorage';
import { useSnackbar } from '../../contexts/SnackbarContext';

const handleDownload = async (taskId, existingUrl) => {
    if (existingUrl) { window.open(existingUrl, '_blank'); return; }
    if (!taskId) { alert('Task ID not available to generate invoice.'); return; }
    try {
        const res = await invoiceService.generateInvoice(taskId);
        const url = res?.data?.invoiceUrl 
        if (url) window.open(url, '_blank');
        else alert('Invoice could not be generated. Please try again.');
    } catch {
        alert('Failed to generate invoice. Please try again.');
    }
};

const InvoiceList = () => {
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [sortField, setSortField] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [paymentInvoice, setPaymentInvoice] = useState(null);
    const [hasPaymentMethods, setHasPaymentMethods] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const itemsPerPage = 10;

    const fetchInvoices = async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const filters = {
                searchQuery,
                sortField,
                startDate,
                endDate,
            };

            const response = await invoiceService.fetchInvoices(page, itemsPerPage, filters);

            if (response && response.data && response.data.success) {
                const data = response.data.data || [];

                if (data.length === 0 && page > 1) {
                    setHasMore(false);
                    setCurrentPage(prev => prev - 1);
                    return;
                }

                const formattedData = data.map(item => ({
                    woNo: item.org_task_code || 'N/A',
                    invoiceNo: item.inv_no || 'N/A',
                    date: item.start_date ? formatDate(item.start_date) : 'N/A',
                    client: item.customer_name || 'N/A',
                    amount: item.amount ? formatCurrency(item.amount) : formatCurrency(0),
                    status: item.payment_status === 0 ? 'Pending' : item.payment_status === 1 ? 'Partially Paid' : item.payment_status === 2 ? 'Paid' : 'Pending',
                    id: item.id,
                    taskId: item.task_id,
                    invoiceUrl: item.invoice_url,
                    paymentStatus: item.payment_status,
                    outstandingAmount: item.outstanding_amount != null ? formatCurrency(item.outstanding_amount) : null,
                    rawOutstandingAmount: item.outstanding_amount != null ? parseFloat(item.outstanding_amount) : null,
                }));

                setInvoices(formattedData);
                const total = response.totalCount || data.length;
                setTotalItems(total);
                setHasMore(page * itemsPerPage < total);
            } else {
                if (page > 1) {
                    setCurrentPage(prev => prev - 1);
                } else {
                    setInvoices([]);
                    setTotalItems(0);
                    setHasMore(false);
                }
            }
        } catch (err) {
            console.error('Error fetching invoices:', err);
            setError('Failed to load invoices.');
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvoices(currentPage);
    }, [currentPage, searchQuery, sortField, startDate, endDate]);

    useEffect(() => {
        const orgGuid = getOrgGuid();
        if (!orgGuid) return;
        paymentService.getOrgPaymentMethods(orgGuid)
            .then((res) => {
                const data = res.data || [];
                const list = Array.isArray(data) ? data : (data.data || []);
                setHasPaymentMethods(list.some(m => m.is_active));
            })
            .catch(() => {});
    }, []);

    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

    const searchTimerRef = React.useRef(null);
    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchInput(val);
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setSearchQuery(val);
            setCurrentPage(1);
        }, 500);
    };

    const handleSort = (field) => {
        setSortField(prev => (prev === field ? `-${field}` : field));
        setCurrentPage(1);
    };

    const getSortIcon = (field) => {
        if (sortField === field) return ' ↑';
        if (sortField === `-${field}`) return ' ↓';
        return '';
    };

    const hasDateFilter = startDate || endDate;
    const hasActiveFilters = searchQuery || startDate || endDate;

    const handleApplyFilters = ({ startDate: s, endDate: e }) => {
        setStartDate(s);
        setEndDate(e);
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setStartDate('');
        setEndDate('');
        setCurrentPage(1);
    };

    const { showSnackbar } = useSnackbar();
    const isMobile = window.innerWidth <= 768;

    const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobileView(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!openMenuId) return;
        const close = () => setOpenMenuId(null);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [openMenuId]);

    const handlePayClick = (invoice) => {
        const raw = invoice.rawOutstandingAmount ?? parseFloat(invoice.amount);
        if (raw === 0) {
            showSnackbar('No payment needed. Outstanding amount is 0.', 'success');
            return;
        }
        setPaymentInvoice(invoice);
    };

    return (
        <div className="task-list-container">
            <HeaderNav profileIcon={profileIcon} />

            {/* Mobile Header */}
            <header className="mobile-header">
                <div className="header-left" onClick={() => navigate('/dashboard')}>
                    <IoArrowUndoOutline color="black" size={25} alt="Back" />
                </div>
                <h2 style={{ fontSize: '16px', fontFamily: 'lato, sans-serif', marginLeft: '10px', color: '#000' }}>Invoice</h2>
                <div className="header-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setIsFilterModalOpen(true)}>
                        <LuFilter size={20} />
                        {hasDateFilter && <span className="filter-indicator">1</span>}
                    </div>
                </div>
            </header>

            {/* Mobile Search Bar */}
            <div className="mobile-search-bar">
                <SearchInput
                    value={searchInput}
                    onChange={handleSearchChange}
                    placeholder="Search invoices..."
                    width="100%"
                />
            </div>

            {/* Desktop Filters Section */}
            <div className="header">
                <h2 className="page-title">Invoice</h2>
                <div className="filters">
                    <SearchInput
                        value={searchInput}
                        onChange={handleSearchChange}
                        placeholder="Search invoices..."
                        width="250px"
                    />
                    <button
                        className="search-button filter-button-container"
                        onClick={() => setIsFilterModalOpen(true)}
                    >
                        <LuFilter size={16} color="white" />
                        {hasDateFilter && <span className="filter-indicator">1</span>}
                    </button>
                </div>
            </div>

            {/* Table Section */}
            <div className="task-table-container" style={{ display: isMobileView ? 'none' : undefined }}>
                {loading ? (
                    <MainLoader />
                ) : invoices.length === 0 ? (
                    <div className="error-container">
                        <div className="error-box">
                            <div className="error-icon">{hasActiveFilters ? '🔍' : '📭'}</div>
                            <div className="error-text">{hasActiveFilters ? 'No invoices match your filters' : 'No Invoices Available'}</div>
                        </div>
                    </div>
                ) : (
                    <DataTableGrid
                        items={invoices}
                        loading={loading}
                        error={error}
                        columns={[
                            { key: 'woNo', title: 'WO No', sortKey: 'org_task_code' },
                            { key: 'invoiceNo', title: 'Inv No', sortKey: 'inv_no' },
                            { key: 'date', title: 'INV Date', sortKey: 'start_date' },
                            { key: 'amount', title: 'Amount', sortKey: 'amount' },
                            { key: 'outstandingAmount', title: 'Outstanding', sortKey: 'outstanding_amount' },
                            { key: 'status', title: 'Status', sortKey: 'payment_status' },
                            {
                                key: 'actions', title: 'Action',
                                render: (invoice) => (
                                    <div className="kebab-wrapper" onClick={e => e.stopPropagation()}>
                                        <button
                                            className="kebab-trigger"
                                            onClick={() => setOpenMenuId(prev => prev === invoice.id ? null : invoice.id)}
                                        >
                                            <BsThreeDotsVertical size={16} />
                                        </button>
                                        {openMenuId === invoice.id && (
                                            <div className="kebab-menu">
                                                <button
                                                    className="kebab-item"
                                                    disabled={!invoice.taskId}
                                                    onClick={() => { setOpenMenuId(null); handleDownload(invoice.taskId, invoice.invoiceUrl); }}
                                                >
                                                    <LuDownload size={13} /> Download
                                                </button>
                                                {invoice.paymentStatus !== 2 && hasPaymentMethods && (
                                                    <button
                                                        className="kebab-item"
                                                        onClick={() => { setOpenMenuId(null); handlePayClick(invoice); }}
                                                    >
                                                        <MdPayment size={13} /> Pay
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )
                            },
                        ]}
                        onSort={handleSort}
                        sortField={sortField}
                        getSortIcon={getSortIcon}
                    />
                )}
            </div>

            {/* Pagination */}
            {!isMobileView && (currentPage > 1 || hasMore) && (
                <div className="pagination">
                    <button
                        className="page-button"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1 || loading}
                    >
                        {"<"}
                    </button>
                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i + 1}
                            className={`page-number-button ${currentPage === i + 1 ? 'active' : ''}`}
                            onClick={() => setCurrentPage(i + 1)}
                            disabled={loading}
                        >
                            {i + 1}
                        </button>
                    ))}
                    <button
                        className="page-button"
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={!hasMore || loading}
                    >
                        {">"}
                    </button>
                </div>
            )}

            {/* Mobile list view */}
            <div className="task-content-container" style={{
                display: isMobileView ? 'flex' : 'none',
                height: isMobile ? 'calc(100vh - 200px)' : 'auto',
                overflowY: isMobile ? 'scroll' : 'visible',
                WebkitOverflowScrolling: 'touch',
                flexDirection: 'column',
                gap: '10px',
                padding: '10px',
                paddingBottom: '80px',
                background: '#F6F8FC'
            }}>
                {loading ? (
                    <MainLoader />
                ) : invoices.length === 0 ? (
                    <div className="mobile-empty-state">
                        <div className="mobile-empty-icon">{hasActiveFilters ? '🔍' : '📭'}</div>
                        <div className="mobile-empty-text">{hasActiveFilters ? 'No invoices match your filters' : 'No Invoices Available'}</div>
                    </div>
                ) : (
                    invoices.map((invoice, index) => (
                        <div key={index} className="task-card-new">
                            <div className="task-top-row">
                                <span className="task-date">{invoice.date}</span>
                                <span className={`task-status status-${invoice.status.toLowerCase().replace(/\s+/g, '-')}`}>{invoice.status}</span>
                            </div>
                            <div className="task-main-row">
                                <div className="task-details">
                                    <h3 className="task-equipment">{invoice.invoiceNo}</h3>
                                    <p className="task-company">WO: {invoice.woNo}</p>
                                    <p className="task-warranty">Amount: {invoice.amount}</p>
                                    {invoice.outstandingAmount != null && (
                                        <p className="task-warranty">Outstanding: {invoice.outstandingAmount}</p>
                                    )}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignSelf: 'flex-start', marginLeft: 'auto' }}>
                                    <button
                                        onClick={() => handleDownload(invoice.taskId, invoice.invoiceUrl)}
                                        disabled={!invoice.taskId}
                                        style={{ background: 'none', border: 'none', cursor: invoice.taskId ? 'pointer' : 'not-allowed', color: invoice.taskId ? '#35605a' : '#ccc', padding: '4px' }}
                                        title="Download Invoice"
                                    >
                                        <LuDownload size={20} />
                                    </button>
                                    {invoice.paymentStatus !== 2 && hasPaymentMethods && (
                                        <button
                                            onClick={() => handlePayClick(invoice)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A2134', padding: '4px' }}
                                            title="Pay Invoice"
                                        >
                                            <MdPayment size={20} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {paymentInvoice && (
                <PaymentModal
                    invoice={paymentInvoice}
                    onClose={() => setPaymentInvoice(null)}
                    onSuccess={() => { setPaymentInvoice(null); fetchInvoices(currentPage); }}
                />
            )}
            <InvoiceFilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                onApplyFilters={handleApplyFilters}
                onClearFilters={handleClearFilters}
                initialFilters={{ startDate, endDate }}
            />
            <MobileFooterNav />
        </div>
    );
};

export default InvoiceList;
